
import React from 'react';
import { mdx } from '@mdx-js/react';


export const frontMatter = {
	id: 'fire-event',
	title: 'Firing an Event'
};
export const contentTitle = undefined;
export const metadata = {
  "unversionedId": "api-reference/fire-event",
  "id": "api-reference/fire-event",
  "isDocsHomePage": false,
  "title": "Firing an Event",
  "description": "All events are fired the same way.  The only difference between them is the payload they send.",
  "source": "@site/docs/api-reference/firing-events.md",
  "sourceDirName": "api-reference",
  "slug": "/api-reference/fire-event",
  "permalink": "/docs/api-reference/fire-event",
  "tags": [],
  "version": "current",
  "frontMatter": {
    "id": "fire-event",
    "title": "Firing an Event"
  },
  "sidebar": "docs",
  "previous": {
    "title": "Overview",
    "permalink": "/docs/api-reference/events-and-actions"
  },
  "next": {
    "title": "AGENCY_OF_RECORD_CHANGED",
    "permalink": "/docs/api-reference/events/agency-of-record-changed"
  }
};


/* @jsxRuntime classic */
/* @jsx mdx */

export const toc = [{
  value: 'Fired event request/response',
  id: 'fired-event-requestresponse',
  children: []
}, {
  value: 'Asynchronous responses to action requests',
  id: 'asynchronous-responses-to-action-requests',
  children: []
}];

const layoutProps = {
  toc
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`All events are fired the same way.  The only difference between them is the payload they send.
Here's a skeletal example:`}</p>
    <p><strong parentName="p">{`POST:`}</strong>{` `}<inlineCode parentName="p">{`https://api.{ENV}.dais.com/ioi/v3/event/fire`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "lineId": "<UUID of the product you are firing the event for>",
  "type": "<event name>",
  "agencyCodes": ["<for some events, agency codes are required>"],
  "payload": {
      <specific to each event>
  }
}
`}</code></pre>
    <p>{`Once the event is fired, there could be many actions that get executed.  Each may be different.  For example,
event GET_QUOTES may have actions that will:`}</p>
    <ol>
      <li parentName="ol">{`Send the email;`}</li>
      <li parentName="ol">{`Hit a web-endpoint and pass the client data;`}</li>
      <li parentName="ol">{`Request a quote from a carrier-integrated API;`}</li>
      <li parentName="ol">{`Use the IOI-Rating action to generate a quote within IOI.`}<br parentName="li"></br>
        {`etc.`}</li>
    </ol>
    <p>{`Any one of these actions could be defined multiple times withing the same event.  For example, IOI Rating action is
often defined multiple times in order to generate a variety of quoting options.`}</p>
    <h2 {...{
      "id": "fired-event-requestresponse"
    }}>{`Fired event request/response`}</h2>
    <p>{`Let's consider we're firing an event `}<a parentName="p" {...{
        "href": "/docs/api-reference/events/get-quotes"
      }}>{`GET_QUOTES`}</a>{`. `}</p>
    <p><strong parentName="p"><em parentName="strong">{`Request Payload`}</em></strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl  --location --request POST 'https://api.{ENV}.dais.com/ioi/v3/event/fire'
      --header 'Authorization: Basic <key>' \\
      --header 'Content-Type: application/json' \\
      --data-raw '{
        "lineId": "1c2383a1-03d1-4630-8a09-f99de2958d0c",
        "type": "GET_QUOTES",
        "payload": {
            "clientId": "<optional field, otherwise UUID of the existing client>",
            "effectiveDate": "2022-09-17",
            "intake": {
              "client.name": {
                "type": "ANSWER",
                "answer": "Company A",
                "qid": "client.name"
              },
              "client.industry.naics": {
                "type": "ANSWER",
                "answer": "451130",
                "qid": "client.industry.naics"
              },
              "addresses": {
                "type": "LOOP",
                "qid": "addresses",
                "iterations": [
                  {
                    "iterationId": "f4c41c8d-ffb6-4883-902b-ab6357fc1a54",
                    "answers": {
                      "client.address1": {
                        "type": "ANSWER",
                        "answer": "123 Main St",
                        "qid": "client.address1"
                      },
                      "client.city": {
                        "type": "ANSWER",
                        "answer": "Chicago",
                        "qid": "client.city"
                      },
                      "client.state": {
                        "type": "ANSWER",
                        "answer": "IL",
                        "qid": "client.state"
                      },
                      "client.zipCode": {
                        "type": "ANSWER",
                        "answer": "60089",
                        "qid": "client.zipCode"
                      }
                    }
                  }
                ]
              }
            }
          }
      }'
`}</code></pre>
    <p>{`The response for this event firing will return a payload that contains 4 data points:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`triggerRequestId`}</inlineCode>{`: This is a unique identifier by which all subsequent action executions are tracked;`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`executedActionCount`}</inlineCode>{`: Specifies how many actions have been executed;`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`expectedResponseCount`}</inlineCode>{`: Specifies how many actions will provide some type of response - in this specific case that means, how many quotes to expect in return;`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`metadata`}</inlineCode>{`: event specific payload.  For this particular event, it returns the `}<inlineCode parentName="li">{`UUID`}</inlineCode>{` of the newly created client (since the `}<inlineCode parentName="li">{`clientId`}</inlineCode>{` was not specified in the request.) `}</li>
    </ol>
    <p><strong parentName="p"><em parentName="strong">{`Response Payload`}</em></strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "triggerRequestId": "67c3d2a1-09d6-4f9e-b15c-cbae6ed491d2",
    "executedActionCount": 7,
    "expectedResponseCount": 3,
    "metadata": {
        "client": {
            "clientId": "3729dca8-fe0a-4d67-ae45-fb0e33de16c2"
        }
    }
}
`}</code></pre>
    <h2 {...{
      "id": "asynchronous-responses-to-action-requests"
    }}>{`Asynchronous responses to action requests`}</h2>
    <p>{`Because of the disjoint nature of the actions, they are executed asynchronously.  However, the response to the
event firing will tell us how many responses to expect - as not all actions generate a response
(email sending, for example).`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;
